import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context';
import { storyCardApi } from '../../../services';
import {
  COMMENTS_FEED_ADMIN_COOKIE_KEY,
  cookieOptions,
  Filter,
  FilterGroup,
  FilterValue,
  OrderDirection,
  useAppDispatch,
  useAuthorsFilter,
  useCardStoryFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFilter,
} from '../../../shared';
import { getCommentsFeed } from '../../../slices';

import classes from './CommentsFeedRightSide.module.scss';

export const CommentsFeedRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByChannelId.useLazyQuery();

  const { getGroupValue, getDateRangeValue } = useFilter();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const [cookies, setCookie] = useCookies([COMMENTS_FEED_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[COMMENTS_FEED_ADMIN_COOKIE_KEY]?.filter ?? [
      {
        groupId: 'orderDirection',
        groupItems: [{ id: OrderDirection.DESC, value: OrderDirection.DESC }],
      },
    ]
  );

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStoryGroup } = useCardStoryFilter({ filter });

  const groups: FilterGroup[] = useMemo(
    () => [
      {
        id: 'orderDirection',
        title: t('common.sort-by'),
        items: [
          {
            id: OrderDirection.DESC,
            label: t(`orderDirection.${OrderDirection.DESC}`),
            value: OrderDirection.DESC,
          },
          {
            id: OrderDirection.ASC,
            label: t(`orderDirection.${OrderDirection.ASC}`),
            value: OrderDirection.ASC,
          },
        ],
      },

      authorsGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStoryGroup,
    ],
    [authorsGroup, cardStoryGroup, cardTypeGroup, dateRangeGroup, t]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const storyId = getGroupValue({ ...commonIdProps, groupId: cardStoryGroup.id });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const orderDirection = getGroupValue({ filter, groupId: 'orderDirection' });

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const common = {
        ...(authorId?.length && { authorId }),
        ...(storyCardType?.length && { storyCardType }),
        ...(storyId?.length && { storyId }),
      };

      const requestFilterToSave = { ...common, orderDirection, dateRange };

      const filters = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        COMMENTS_FEED_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      setFilter(filter);

      dispatch(getCommentsFeed({ channelId, orderDirection, filters }));
    },
    [
      authorsGroup.id,
      cardStoryGroup.id,
      cardTypeGroup.id,
      channelId,
      dateRangeGroup.id,
      dispatch,
      getDateRangeValue,
      getGroupValue,
      setCookie,
    ]
  );

  return (
    <div className={classes['comments-feed-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
